import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({ 
  currentPage, 
  totalItems, 
  itemsPerPage, 
  onPageChange, 
  onItemsPerPageChange 
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Local input state for the "Go to page" jump — commits only on
  // Enter / blur / Go-click, not on every keystroke. This lets an admin
  // type "10" without the app racing to fetch page 1 first.
  const [jumpValue, setJumpValue] = useState('');
  useEffect(() => { setJumpValue(''); }, [currentPage]);

  const commitJump = () => {
    const raw = jumpValue.trim();
    if (!raw) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) { setJumpValue(''); return; }
    const clamped = Math.min(totalPages, Math.max(1, n));
    if (clamped !== currentPage) onPageChange(clamped);
    setJumpValue('');
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 bg-white">
      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Show</span>
          <Select 
            value={itemsPerPage.toString()} 
            onValueChange={(value) => onItemsPerPageChange(parseInt(value))}
          >
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="30">30</SelectItem>
              <SelectItem value="40">40</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-700">per page</span>
        </div>
        
        <div className="text-sm text-gray-700 text-center sm:text-left">
          <span className="font-medium">{startItem}</span> - <span className="font-medium">{endItem}</span> of <span className="font-medium">{totalItems}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Previous</span>
        </Button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">...</span>
            ) : (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(page)}
                className={currentPage === page ? "bg-[#0A7EA4] hover:bg-[#0A7EA4]/90" : ""}
              >
                {page}
              </Button>
            )
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="gap-1"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4" />
        </Button>

        {/* Jump-to-page — appears only when the list is long enough to
            warrant it. Commits on Enter, blur, or Go click (never per
            keystroke) so typing "10" doesn't race a fetch at 1. Sits on
            its own wrapped row on narrow screens via the parent's
            flex-wrap so mobile pagination never overflows horizontally. */}
        {totalPages > 5 && (
          <div className="flex items-center gap-1 sm:pl-2 sm:border-l sm:border-gray-200 sm:ml-1 basis-full sm:basis-auto justify-center">
            <span className="text-xs text-gray-500 hidden sm:inline">Go to</span>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitJump(); } }}
              onBlur={commitJump}
              placeholder={`1-${totalPages}`}
              className="h-8 w-16 text-sm"
              aria-label="Jump to page"
              data-testid="pagination-jump-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={commitJump}
              disabled={!jumpValue.trim()}
              className="h-8 px-2"
              data-testid="pagination-jump-btn"
            >
              Go
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
